import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { VisuallyHidden } from "radix-ui";
import { useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { t } from "ttag";
import { z } from "zod";

import { getDocumentQuery, updateDocument } from "@/app/api/legacy-api";
import {
  DOCUMENT_ICON,
  getDocumentEditNameLabel,
} from "@/app/components/legacy-components";
import { LogoLoader } from "@/shared/components";
import type { DocumentFolder, ModalProps } from "@/shared/models/legacy-types";
import {
  Button,
  Dialog,
  DocumentAddIcon,
  errorToast,
  Form,
  Input,
  useToastStore,
} from "@/shared/ui";

const formSchema = z.object({
  name: z.string().min(1, { error: () => t`Name is required` }),
});

export const EditDocument = ({ show, onClose }: ModalProps) => {
  const { pushToast } = useToastStore();
  const { documentId, folder } = useParams<{
    documentId: string;
    folder: DocumentFolder;
  }>();
  const [searchParams] = useSearchParams();
  const vendor = searchParams.get("vendor");
  const { data, isLoading, isSuccess } = useQuery(
    getDocumentQuery(documentId, vendor),
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    values: { name: data?.name ?? "" },
  });

  const queryClient = useQueryClient();
  const { mutate: updateDocumentMutation, isPending } = useMutation({
    mutationFn: updateDocument.mutation,
    onSuccess: () => {
      onClose();
      updateDocument.invalidates(queryClient);
      void pushToast({
        type: "success",
        title: t`Success`,
        message: t`Document successfully updated!`,
      });
    },
    onError: errorToast,
  });

  if (!folder || !documentId) {
    return null;
  }

  const lowerCaseFolderLabel = getDocumentEditNameLabel(folder).toLowerCase();

  return (
    <Dialog.Root open={show} onOpenChange={onClose}>
      <LogoLoader
        show={isLoading || isPending}
        withOverlay
        fullscreen={isPending}
      />

      {isSuccess && (
        <Dialog.Content>
          <Dialog.Header icon={DocumentAddIcon}>
            <Dialog.Title>{t`Edit ${lowerCaseFolderLabel}`}</Dialog.Title>

            <VisuallyHidden.Root asChild>
              <Dialog.Description>
                {t`This modal lets you update the name of the document.`}
              </Dialog.Description>
            </VisuallyHidden.Root>
          </Dialog.Header>

          <Form.Provider {...form}>
            <form
              onSubmit={form.handleSubmit((value) =>
                updateDocumentMutation({ ...value, id: documentId, vendor }),
              )}
              className="flex flex-col gap-6"
            >
              <Form.Field
                control={form.control}
                name="name"
                render={() => (
                  <Form.Item>
                    <Form.Label>{getDocumentEditNameLabel(folder)}</Form.Label>
                    <Form.Control>
                      <Input.Root {...form.register("name")}>
                        <Input.Slot>{DOCUMENT_ICON[folder]}</Input.Slot>
                      </Input.Root>
                    </Form.Control>
                    <Form.Message compact={false} />
                  </Form.Item>
                )}
              />

              <Dialog.Footer>
                <Button onClick={onClose} size="lg" variant="secondary">
                  {form.formState.isDirty ? t`Discard change` : t`Cancel`}
                </Button>

                <Button
                  disabled={!form.formState.isDirty || isPending}
                  size="lg"
                  type="submit"
                >
                  {t`Save change`}
                </Button>
              </Dialog.Footer>
            </form>
          </Form.Provider>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
};
